"use client";
import cn from "classnames";

import { useEffect, useRef, type ReactNode } from "react";

import { CloseIcon } from "@/components/icons/close.icon";

export const QuizModal = ({
  open,
  onClose,
  position = "center",
  children,
}: QuizModalProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open) {
      dialog.showModal();
    } else {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };

    dialog.addEventListener("cancel", handleCancel);
    return () => dialog.removeEventListener("cancel", handleCancel);
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current) {
      onClose();
    }
  };

  const handleCenterBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <dialog
      ref={dialogRef}
      data-testid="quiz-modal"
      onClick={position === "right" ? handleBackdropClick : undefined}
      className={cn({
        "fixed inset-0 z-50 m-0 h-full max-h-none w-full max-w-none bg-transparent p-0 backdrop:bg-black/50":
          position === "center",
        "fixed inset-0 z-50 ml-auto h-full max-h-full w-full max-w-125 overflow-y-auto rounded-none bg-transparent p-0 backdrop:bg-black/50 md:w-2/3 lg:w-125":
          position === "right",
      })}>
      {position === "center" ? (
        <div
          className="flex h-full w-full items-center justify-center p-4"
          onClick={handleCenterBackdropClick}>
          <div className="relative max-h-[90vh] w-full max-w-120 overflow-y-auto rounded-2xl bg-white px-8 py-10">
            <button
              data-testid="quiz-modal-close"
              onClick={onClose}
              aria-label="Close modal"
              className="text-greyscale-strength hover:text-greyscale-headline absolute top-4 right-4 cursor-pointer leading-none">
              <CloseIcon width={16} height={16} />
            </button>
            {open && children}
          </div>
        </div>
      ) : (
        <div className="relative rounded-2xl bg-white px-8 py-10">
          <button
            data-testid="quiz-modal-close"
            onClick={onClose}
            aria-label="Close modal"
            className="text-greyscale-strength hover:text-greyscale-headline absolute top-4 right-4 cursor-pointer leading-none">
            <CloseIcon width={16} height={16} />
          </button>
          {open && children}
        </div>
      )}
    </dialog>
  );
};

type QuizModalPosition = "center" | "right";

type QuizModalProps = {
  open: boolean;
  onClose: () => void;
  position?: QuizModalPosition;
  children: ReactNode;
};
